// Hidden developer page listing UI Japanese strings that showed up on
// screen without an English equivalent in the runtime dictionary.
// Route: /i18n-report (see App.js).
import { useEffect, useState } from 'react';
import { getMissing, clearMissing, subscribeMissing } from '@/i18n/MissingKeys';
import { useT } from '@/i18n/LanguageContext';

// Escape a Japanese string so it can be used inside a JS single-quoted literal.
function escapeJsSingle(s) {
  return String(s).replace(/\\/g, '\\\\').replace(/'/g, "\\'");
}

export default function I18nReport() {
  const { lang, setLang } = useT();
  const [rows, setRows] = useState(getMissing());
  const [filter, setFilter] = useState('');
  const [format, setFormat] = useState('js'); // 'js' or 'json'
  const [copied, setCopied] = useState(false);
  useEffect(() => subscribeMissing(() => setRows(getMissing())), []);
  useEffect(() => { const id = setInterval(() => setRows(getMissing()), 1500); return () => clearInterval(id); }, []);
  const filtered = rows.filter((r) => !filter || r.ja.includes(filter));
  const copy = () => {
    let text;
    if (format === 'js') {
      // Diagnostic list only — do NOT hand-write English here; the local LLM
      // is the single translation path.
      text = filtered.map((r) => `  '${escapeJsSingle(r.ja)}',`).join('\n');
    } else {
      text = JSON.stringify(filtered.reduce((a, r) => { a[r.ja] = ''; return a; }, {}), null, 2);
    }
    try { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* noop */ }
  };
  const download = () => {
    const rowsCsv = [
      'japanese,count,sample',
      ...filtered.map((r) => {
        const j = `"${String(r.ja).replace(/"/g, '""')}"`;
        const s = `"${(r.samples || []).join('; ').replace(/"/g, '""')}"`;
        return `${j},${r.count},${s}`;
      }),
    ].join('\n');
    const blob = new Blob([rowsCsv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'i18n-missing.csv';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  return (
    <div className="p-6 max-w-4xl mx-auto" data-testid="i18n-report-page">
      <h1 className="text-2xl font-bold mb-2">i18n translation report</h1>
      <p className="text-sm text-zinc-600 mb-1">
        Japanese UI phrases observed on screen while running in <b>{lang}</b> mode
        that the local translation model did NOT return English for (skipped,
        failed, or protected identifiers).
      </p>
      <p className="text-xs text-zinc-500 mb-4">
        <b>Note:</b> English is generated on demand by the configured local LLM
        (AI管理 → 機能ルーティング → 翻訳) — there is no hardcoded Japanese→English
        dictionary any more. Entries listed here stayed Japanese on purpose
        (identifiers, numbers, filenames) or because a translation request failed.
        Use this list to diagnose translation coverage, not to hand-write English.
      </p>
      <div className="flex flex-wrap gap-2 mb-3 items-center">
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="filter…"
          className="border rounded px-2 py-1 flex-1 min-w-[180px]"
          data-testid="i18n-report-filter"
        />
        <label className="text-xs text-zinc-600 flex items-center gap-1">
          format:
          <select
            value={format}
            onChange={(e) => setFormat(e.target.value)}
            className="border rounded px-1 py-1 text-xs"
            data-testid="i18n-report-format"
          >
            <option value="js">JS (list)</option>
            <option value="json">JSON</option>
          </select>
        </label>
        <button
          onClick={() => setLang(lang === 'en' ? 'ja' : 'en')}
          className="border rounded px-3 py-1 text-sm"
          data-testid="i18n-report-toggle-lang"
        >Toggle EN/JA</button>
        <button
          onClick={copy}
          className="border rounded px-3 py-1 text-sm"
          data-testid="i18n-report-copy"
        >{copied ? 'Copied ✓' : 'Copy'}</button>
        <button
          onClick={download}
          className="border rounded px-3 py-1 text-sm"
          data-testid="i18n-report-csv"
        >Download CSV</button>
        <button
          onClick={() => { clearMissing(); setRows([]); }}
          className="border rounded px-3 py-1 text-sm"
          data-testid="i18n-report-clear"
        >Clear</button>
      </div>
      <div className="text-xs text-zinc-500 mb-2">{filtered.length} entries</div>
      <table className="w-full text-sm border">
        <thead className="bg-zinc-100 border-b-2 border-zinc-300">
          <tr>
            <th className="text-left p-2 border-b">Japanese</th>
            <th className="text-right p-2 border-b w-20">count</th>
            <th className="text-left p-2 border-b w-64">seen in</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((r) => (
            <tr key={r.ja} className="border-b">
              <td className="p-2 font-mono">{r.ja}</td>
              <td className="p-2 text-right">{r.count}</td>
              <td className="p-2 text-zinc-500 text-xs">{r.samples.join(', ')}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {filtered.length === 0 && (
        <div className="text-sm text-zinc-500 text-center p-8" data-testid="i18n-report-empty">
          No missing keys. Navigate through the app to populate.
        </div>
      )}
    </div>
  );
}
